<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import { apiGetPreviewUrl } from '@ilteducation/content';
import ContentHoverCard from '../ContentHoverCard.vue';
import ContentCoverStack from '../ContentCoverStack.vue';
import useCoverDimensions from '../helpers/use-cover-dimensions';
import dummyImageUrl from '../assets/materials/dummy-cover.png';

type Props = {
  material: MaterialV1;
  duplicatesCount?: number;
  hoverOverflowHidden?: boolean;
  rounded?: boolean;
  hoverRoundedClass?: string;
  selected?: boolean;
  showTitle?: boolean;
  selectingMode?: boolean;
  selectLabel?: string;
  deselectLabel?: string;
  slotWidth?: number;
  maxWidth?: number;
  maxHeight?: number;
};

const props = withDefaults(defineProps<Props>(), {
  duplicatesCount: 0,
  rounded: true,
  hoverRoundedClass: 'rounded-lg', // On Polylino we use rounded-lg, in Manabua we use rounded-sm
  hoverOverflowHidden: true,
  selected: false,
  showTitle: true,
  selectingMode: false,
  selectLabel: undefined,
  deselectLabel: undefined,
  slotWidth: undefined,
  maxWidth: 180,
  maxHeight: 180,
});

const { coverRef, coverHeight, coverWidth, onImageLoaded } = useCoverDimensions(props.maxWidth, props.maxHeight);
const emit = defineEmits<{
  (e: 'onMaterialClick', material: MaterialV1): void;
}>();
const { material, duplicatesCount } = toRefs(props);

const dummyImage = dummyImageUrl;
const previewUrl = apiGetPreviewUrl(material.value.id, 500); // define the image height for the preview from server;
const hasStack = computed(() => duplicatesCount.value > 1);
const id = computed(() => material.value.id);

const placeHolder = ref(
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mO88R8AArUB2TtNwp8AAAAASUVORK5CYII=',
);
</script>

<template>
  <div class="cover-slot" :style="slotWidth ? { width: `${slotWidth}px` } : undefined">
    <ContentHoverCard
      v-memo="[id, material.title, coverWidth, coverHeight, duplicatesCount]"
      :data-material-id="id"
      :title="material?.title"
      :image-height="coverHeight"
      :has-stack="hasStack"
      :overflow-hidden="hoverOverflowHidden"
      :is-rounded="rounded"
      :hover-rounded-class="hoverRoundedClass"
      :selected="selected"
      :content-card-width="maxWidth"
      :content-card-height="maxHeight"
      :title-width="coverWidth"
      @on-card-click="emit('onMaterialClick', material)"
      @on-card-select="emit('onMaterialClick', material)"
    >
      <template #coverFeatures>
        <slot name="coverFeatures"></slot>
      </template>
      <template #image>
        <v-btn
          tabindex="0"
          variant="text"
          :width="coverWidth"
          :height="coverHeight"
          :data-testid="`material-${material.id}`"
        >
          <v-img
            v-memo="[material.id, previewUrl, coverWidth, coverHeight, material.title]"
            ref="coverRef"
            id="material-cover"
            class="bg-surface-container-low elevation-4"
            :width="coverWidth"
            :height="coverHeight"
            :src="previewUrl"
            :lazy-src="placeHolder"
            :alt="material.title"
            cover
            @load="onImageLoaded(maxWidth, maxHeight)"
          >
            <template #error>
              <v-img height="100%" :src="dummyImage" :alt="material.title" cover />
            </template>
          </v-img>
          <div v-if="selectingMode || $slots.imageOverlay" class="image-overlay">
            <v-btn
              v-if="selectingMode"
              class="select-btn"
              :color="selected ? 'primary' : 'secondary'"
              icon
              size="x-small"
              tabindex="-1"
              :aria-label="selected ? deselectLabel : selectLabel"
            >
              <v-icon>{{ selected ? 'mdi-check' : '' }}</v-icon>
            </v-btn>
            <slot name="imageOverlay"></slot>
          </div>
        </v-btn>
      </template>
      <template v-if="hasStack && coverWidth && coverHeight" #stack>
        <ContentCoverStack
          :width="coverWidth"
          :height="coverHeight"
          :src="previewUrl"
          :level="duplicatesCount"
          :alt="material.title"
          aria-hidden="true"
        >
          <template #error>
            <v-img height="100%" :src="dummyImage" :alt="material.title" cover />
          </template>
        </ContentCoverStack>
      </template>
      <template v-if="showTitle" #title>
        <div class="mt-2">
          <p :aria-hidden="true" class="text-2 material-ttl">{{ material.title }}</p>
        </div>
      </template>
    </ContentHoverCard>
  </div>
</template>

<style scoped lang="scss">
.material {
  cursor: pointer;
  outline: none !important;
  border: none !important;
}
// Uniform cell for the grid; the cover is centred and keeps its own aspect ratio.
.cover-slot {
  display: flex;
  justify-content: center;
}

:deep(.content-card-item) {
  display: flex;
}
:deep(.v-card-item__content) {
  display: flex;
  justify-content: center;
  align-self: flex-end;
  width: 100%;
}

.material-ttl {
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.25;
  height: 3.75em;
}

.text-on-surface-medium {
  color: rgb(var(--v-theme-on-surface-medium));
}
.image-overlay {
  position: absolute;
  bottom: 15%;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
}

.select-btn {
  font-size: 15px;
  border: 2px solid #fff;
}
</style>
